import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Play, Pencil, Trash2 } from 'lucide-react';
import { projectsAPI } from '../../api/projects';
import { getApiBaseUrl } from '../../api/apiConfig';
import { showError, showSuccess, confirmDialog } from '../../utils/toast';
import './ProjectList.css';

const ProjectList = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await projectsAPI.getAll();
      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      showError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog('Are you sure you want to delete this project?');
    if (!confirmed) return;

    try {
      await projectsAPI.delete(id);
      showSuccess('Project deleted successfully');
      fetchProjects();
    } catch (error) {
      showError('Failed to delete project');
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const base = getApiBaseUrl();
    return `${base}${path.startsWith('/') ? path : '/' + path}`;
  };

  if (loading) {
    return (
      <div className="project-list-loading">
        <div className="loading-dots">
          <span />
          <span />
          <span />
        </div>
        <p>Loading projects...</p>
      </div>
    );
  }

  return (
    <motion.div
      className="project-list-studio"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="project-list-header">
        <div>
          <h1 className="project-list-title font-display">Projects</h1>
          <p className="project-list-subtitle">
            Your portfolio and multimedia work
          </p>
        </div>
        <Link to="/projects/new" className="btn-primary">
          <Plus size={18} />
          New Project
        </Link>
      </div>

      {projects.length === 0 ? (
        <div className="project-list-empty">
          <div className="empty-illustration">
            <Play size={64} strokeWidth={1.5} />
          </div>
          <h3>No projects yet</h3>
          <p>Create your first project to showcase your work</p>
          <Link to="/projects/new" className="btn-primary">
            <Plus size={18} />
            Add Project
          </Link>
        </div>
      ) : (
        <div className="project-gallery-grid">
          <AnimatePresence mode="popLayout">
            {projects.map((project, i) => (
              <motion.div
                key={project.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: i * 0.03, duration: 0.25 }}
                className="project-gallery-card"
              >
                <Link
                  to={`/projects/${project.id}`}
                  className="project-card-link"
                >
                  <div className="project-card-media">
                    {project.cover_image ? (
                      <img
                        src={getImageUrl(project.cover_image)}
                        alt={project.title}
                        className="project-card-img"
                      />
                    ) : (
                      <div className="project-card-placeholder">
                        <Play size={40} strokeWidth={1.5} />
                      </div>
                    )}
                    <div className="project-card-overlay">
                      <span className="overlay-action">
                        <Pencil size={18} />
                        Edit
                      </span>
                    </div>
                  </div>
                  <div className="project-card-body">
                    <h3 className="project-card-title">{project.title}</h3>
                    <span className="project-card-category">{project.category}</span>
                    <span className="project-card-date">
                      {new Date(project.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </Link>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleDelete(project.id);
                  }}
                  className="project-card-delete"
                  aria-label="Delete project"
                >
                  <Trash2 size={16} />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};

export default ProjectList;
